import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, MessageSquareReply, Users } from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [reviewsHealth, setReviewsHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchStats();
    fetchReviewsHealth();
  }, []);

  const fetchReviewsHealth = async () => {
    try {
      const [gRes, sRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/google`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/review-responses/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      const gData = await gRes.json();
      const sData = await sRes.json();
      setReviewsHealth({
        rating: gData.rating,
        totalReviews: gData.total_reviews,
        pendingReplies: sData?.stats?.pending ?? 0
      });
    } catch (err) {
      console.error('Failed to fetch reviews health:', err);
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch A/B test summary
      const abRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ab/analytics/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const abData = await abRes.json();

      // Fetch specials count
      const specialsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/specials/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const specialsData = await specialsRes.json();

      setStats({
        abTests: abData.summary || [],
        specials: specialsData.specials || [],
        totalViews: abData.summary?.reduce((sum, t) => sum + t.total_views, 0) || 0,
        totalConversions: abData.summary?.reduce((sum, t) => sum + t.total_conversions, 0) || 0
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-teal-600">{stats?.abTests?.length || 0}</div>
          <div className="text-gray-600">Active A/B Tests</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-blue-600">{stats?.totalViews || 0}</div>
          <div className="text-gray-600">Total Test Views</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-green-600">{stats?.totalConversions || 0}</div>
          <div className="text-gray-600">Total Conversions</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-purple-600">{stats?.specials?.length || 0}</div>
          <div className="text-gray-600">Active Specials</div>
        </div>
      </div>

      {/* Reviews Health */}
      <Link
        to="/admin/review-responses"
        data-testid="reviews-health-tile"
        className="block bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Reviews Health</h2>
          <span className="text-sm text-teal-600 font-medium">Manage replies →</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="flex items-center gap-3" data-testid="reviews-health-rating">
            <div className="w-11 h-11 rounded-lg bg-yellow-50 flex items-center justify-center shrink-0">
              <Star className="w-6 h-6 text-yellow-500 fill-yellow-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {reviewsHealth?.rating != null ? reviewsHealth.rating.toFixed(1) : '—'}
              </div>
              <div className="text-sm text-gray-500">Google Rating</div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="reviews-health-count">
            <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {reviewsHealth?.totalReviews != null ? reviewsHealth.totalReviews.toLocaleString() : '—'}
              </div>
              <div className="text-sm text-gray-500">Total Reviews</div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="reviews-health-pending">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${reviewsHealth?.pendingReplies > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
              <MessageSquareReply className={`w-6 h-6 ${reviewsHealth?.pendingReplies > 0 ? 'text-orange-500' : 'text-green-500'}`} />
            </div>
            <div>
              <div className={`text-2xl font-bold ${reviewsHealth?.pendingReplies > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                {reviewsHealth?.pendingReplies ?? '—'}
              </div>
              <div className="text-sm text-gray-500">Replies Pending Approval</div>
            </div>
          </div>
        </div>
      </Link>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/specials" className="p-4 bg-teal-50 rounded-lg text-center hover:bg-teal-100 transition">
            <div className="text-2xl mb-2">🎁</div>
            <div className="font-medium text-gray-800">Edit Specials</div>
          </Link>
          <Link to="/admin/homepage" className="p-4 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition">
            <div className="text-2xl mb-2">🏠</div>
            <div className="font-medium text-gray-800">Edit Homepage</div>
          </Link>
          <Link to="/admin/ab-tests" className="p-4 bg-purple-50 rounded-lg text-center hover:bg-purple-100 transition">
            <div className="text-2xl mb-2">🧪</div>
            <div className="font-medium text-gray-800">Create A/B Test</div>
          </Link>
          <Link to="/admin/analytics" className="p-4 bg-green-50 rounded-lg text-center hover:bg-green-100 transition">
            <div className="text-2xl mb-2">📈</div>
            <div className="font-medium text-gray-800">View Analytics</div>
          </Link>
        </div>
      </div>

      {/* Recent A/B Tests */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">A/B Test Performance</h2>
        {stats?.abTests?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4">Test Name</th>
                  <th className="text-left py-3 px-4">Page</th>
                  <th className="text-right py-3 px-4">Views</th>
                  <th className="text-right py-3 px-4">Conversions</th>
                  <th className="text-right py-3 px-4">Conv. Rate</th>
                  <th className="text-center py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.abTests.map((test) => (
                  <tr key={test.test.id} className="border-t">
                    <td className="py-3 px-4 font-medium">{test.test.name}</td>
                    <td className="py-3 px-4 text-gray-600">{test.test.page}</td>
                    <td className="py-3 px-4 text-right">{test.total_views}</td>
                    <td className="py-3 px-4 text-right">{test.total_conversions}</td>
                    <td className="py-3 px-4 text-right font-semibold text-green-600">
                      {test.conversion_rate}%
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        test.test.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {test.test.active ? 'Active' : 'Paused'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No A/B tests yet. <Link to="/admin/ab-tests" className="text-teal-600 hover:underline">Create one</Link></p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
